import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import './Report.scss';
import ReportType from '../../components/ReportType';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Localization from '../../config/localization';

import * as AdminPartnersAidsApi from '../../api/adminPartnersAids';
import * as AdminPartnersAdditionsApi from '../../api/adminPartnersAdditions';
import Notifications from '../../lib/notifications';
import DownloadSvg from '../../components/Svg/download';
import CloseSvg from '../../components/Svg/close';
import { DownloadFile, ExportDate, TimeOut } from '../../lib/utils';
import CustomFile from '../../components/File';
import { isExportCanceled, isExportCompleted, isExportFailed, isExportStarted } from '../../lib/export';
import Loader from '../../components/Loader';
import { fileExtensionTemplate } from '../../constants/file';
import { isJobReportPending } from '../../constants/imports';

type IProps = ReportJob.IProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

class ReportJob extends React.Component<IProps, ReportJob.IState> {

  api = this.props.reportType === 'exportBeneficiary' ? AdminPartnersAidsApi : AdminPartnersAdditionsApi;

  state: ReportJob.IState = {
    showModal: false,

    exportId: undefined,
    jobs: undefined
  };


  renderDownloadButton = (job: Job.IJobExport) => {
    return (
      <div className="d-inline-flex my-auto c-pointer ml-auto mr-5 h-100 eon-red"
        onClick={() => this._downloadJob(job)}>
        <DownloadSvg className="mx-1 mr-2 my-auto" />
        <p className="m-0">{Localization.partnerExportDownloadButton}</p>
      </div>
    );
  };

  renderCancelButton = () => {
    return (
      <div className="d-inline-flex my-auto c-pointer ml-auto mr-5 h-100" onClick={() => this._cancelJob()}>
        <CloseSvg className="mx-1 mr-2 my-auto" />
        <p className="m-0">{Localization.partnerExportCancelButton}</p>
      </div>
    );
  };


  renderExports = () => {
    const { jobs } = this.state;
    if (!jobs || !jobs.length || this.props.reportType !== this.props.currentReportType) return null;

    return jobs.map((dbExport, index) => (
      <CustomFile
        renderProgress={false}
        containerClass={index ? 'mt-4' : ''}
        key={dbExport.jobId}
        fileName={`${this.props.label} - ${ExportDate(dbExport.createdDate)}`}
        fileSize={dbExport.fileSize}
        renderProgressBar={<Loader className="black" loading fixed={false} />}
        isReady={isExportCompleted(dbExport.status)}
        progressColor="eon-turquoise"
        downloadButton={this.renderDownloadButton(dbExport)}
        cancelButton={this.renderCancelButton()}
      />
    ));
  };

  render() {
    return <>
      <ReportType {...this.props as any} SetValue={this._showJobModal} />

      <div className="export-job-container">
        {this.renderExports()}
      </div>
      <Modal isOpen={this.state.showModal} centered toggle={this._closeJobModal}>
        <ModalHeader className="eon-title" tag="h4">
          {Localization.formatString(Localization.reportJobModalTitle, this.props.message)}
        </ModalHeader>
        <ModalBody>
          {Localization.reportJobModalBody}
        </ModalBody>
        <ModalFooter>
          <button className="eon-button bg-eon-red eon-white mx-5" onClick={this._startJob}>
            <span className="px-3">{Localization.reportJobModalButtonSubmit}</span>
          </button>
          <button className="eon-button bg-eon-lightgrey eon-middle-grey mx-5" onClick={this._closeJobModal}>
            <span className="px-3">{Localization.reportJobModalButtonClose}</span>
          </button>
        </ModalFooter>
      </Modal>
    </>;
  }

  _getJobStatus = async (callback?: () => void): Promise<any> => {
    try {
      const cityHallId = this.props.selectedCityHall && this.props.selectedCityHall.cityHallId;

      const response = await this.api.GetJobStatus(cityHallId);

      this.setState({ jobs: response.data });
      if (callback) callback();

      const job = response.data && response.data[0];

      if (job) {
        if (isExportStarted(job.status)) {
          await TimeOut(1000);
          return this._getJobStatus();
        } else if (isExportCanceled(job.status)) {
          Notifications.warning(Localization.reportJobCanceledMessage);
        } else if (isExportFailed(job.status)) {
          Notifications.error(Localization.reportJobErrorMessage);
        } else if (isExportCompleted(job.status)) {
          Notifications.success(Localization.reportJobReadyMessage);
        }
      }
    } catch (error) {
      Notifications.error(error && error.message);

    }

    return undefined;
  };

  _downloadJob = async (job: Job.IJobExport) => {
    try {
      if (!job.partnerExportId) return null;

      const response = await this.api.GerJobFile(job.partnerExportId);

      await DownloadFile(response.data, `${this.props.reportLabel}-${ExportDate(job.createdDate)}.${fileExtensionTemplate}`, { type: response.headers['content-type'] });

    } catch (error) {
      Notifications.error(error && error.message);

    }

    return undefined;
  };

  _getExportId = (data: Job.IJobExport) => (data.aidsExportId || data.additionsExportId) as number;

  _startJob = async () => {
    try {
      const response = await this.api.StartJob(this.props.selectedCityHall && this.props.selectedCityHall.cityHallId, this.props.season);
      this.setState({ exportId: this._getExportId(response.data) });

      this._closeJobModal();

      await this._getJobStatus();
    } catch (error) {
      Notifications.error(error && error.message);
    }
  };

  _cancelJob = async () => {
    try {
      if (this.state.exportId) {
        await this.api.CancelJob(this.state.exportId);

        this.setState({ jobs: undefined, exportId: undefined, showModal: false });
      }
    } catch (error) {
      Notifications.error(error && error.message);
    }
  };

  _closeJobModal = () => {
    this.setState({ showModal: false });
  };

  _showJobModal = async (event: any) => {
    await this._getJobStatus(
      ()=>{
        const showModal = this.state.jobs ? !this.state.jobs.find(job => isJobReportPending(job)) : true;
    
        this.setState({ showModal });
        this.props.SetValue(event)
      }
    );

  };
}

const mapStateToProps = (state: General.Store) => ({ Auth: state.Auth });

const mapDispatchToProps = (dispatch: Dispatch) => ({ ...bindActionCreators({}, dispatch) });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportJob);
